import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

/** The Chrome/Android `beforeinstallprompt` event (not in the TS DOM lib). */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/**
 * "Add to Home Screen" (PWA install). On Android/Chrome we capture the
 * `beforeinstallprompt` event and fire it on demand; iOS Safari has no such
 * event, so the UI falls back to manual Share → Add to Home Screen.
 */
@Injectable({ providedIn: 'root' })
export class InstallService {
  private deferred: BeforeInstallPromptEvent | null = null;
  private readonly _canInstall = new BehaviorSubject<boolean>(false);
  readonly canInstall$ = this._canInstall.asObservable();
  private manifestUrl: string | null = null;

  constructor() {
    window.addEventListener('beforeinstallprompt', (e: Event) => {
      e.preventDefault(); // stop Chrome's mini-infobar; we trigger it ourselves
      this.deferred = e as BeforeInstallPromptEvent;
      this._canInstall.next(true);
    });
    window.addEventListener('appinstalled', () => {
      this.deferred = null;
      this._canInstall.next(false);
    });
  }

  /** A native install prompt is ready (Android/Chrome). */
  get canInstall(): boolean { return !!this.deferred; }

  /** Already running as an installed app. */
  get isStandalone(): boolean {
    return window.matchMedia?.('(display-mode: standalone)').matches || (navigator as any).standalone === true;
  }

  /** iOS Safari — install is manual (Share → Add to Home Screen). */
  get isIOS(): boolean {
    const ua = navigator.userAgent || '';
    return /iphone|ipad|ipod/i.test(ua) && !(window as any).MSStream;
  }

  /**
   * Real iOS Safari — the only iOS browser that can "Add to Home Screen".
   * iOS Chrome/Firefox/Edge and in-app webviews can't, so we tell the user to
   * open the page in Safari instead.
   */
  get isIOSSafari(): boolean {
    if (!this.isIOS) return false;
    const ua = navigator.userAgent || '';
    return /safari/i.test(ua) && !/crios|fxios|edgios|opios|gsa/i.test(ua);
  }

  /** Roughly, are we on a phone/tablet (where a home-screen shortcut makes sense). */
  get isMobile(): boolean {
    return /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent || '');
  }

  /**
   * Build the PWA manifest at runtime from the brand (GET /api/v1/brand) and
   * point the home-screen / install icon at the brand's `logo_url`. A valid
   * ≥192px icon is what makes Chrome offer "Install", so this both fixes
   * installability and makes the icon the tenant's logo (no bundled asset).
   *
   * The manifest is injected as a Blob URL, so every URL inside it MUST be
   * absolute — relative paths would resolve against the blob: URL and break.
   */
  applyBrand(opts: { name?: string; logoUrl?: string; faviconUrl?: string; themeColor?: string; bgColor?: string }): void {
    if (typeof document === 'undefined') return;

    if (opts.themeColor) {
      const meta = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement | null;
      if (meta) meta.content = opts.themeColor;
    }

    // Browser-tab favicon uses the dedicated favicon_url (falls back to the logo).
    const faviconHref = opts.faviconUrl || opts.logoUrl;
    if (faviconHref) this.setFavicon(faviconHref);

    const icon = opts.logoUrl;
    if (!icon) return; // no brand logo yet → keep the bundled fallback

    const origin = window.location.origin;
    const name = opts.name || 'Online Ordering';
    const manifest = {
      name,
      short_name: (opts.name || 'Order').slice(0, 12),
      start_url: origin + '/',
      scope: origin + '/',
      display: 'standalone',
      orientation: 'portrait',
      background_color: opts.bgColor || '#ffffff',
      theme_color: opts.themeColor || '#221110',
      // Same URL declared at both sizes so Chrome accepts it as installable.
      icons: [
        { src: icon, sizes: '192x192', purpose: 'any' },
        { src: icon, sizes: '512x512', purpose: 'any' },
      ],
    };

    const blob = new Blob([JSON.stringify(manifest)], { type: 'application/manifest+json' });
    const url = URL.createObjectURL(blob);
    let link = document.querySelector('link[rel="manifest"]') as HTMLLinkElement | null;
    if (!link) { link = document.createElement('link'); link.rel = 'manifest'; document.head.appendChild(link); }
    if (this.manifestUrl) URL.revokeObjectURL(this.manifestUrl);
    link.href = url;
    this.manifestUrl = url;

    // iOS home-screen icon (no manifest support there).
    let apple = document.querySelector('link[rel="apple-touch-icon"]') as HTMLLinkElement | null;
    if (!apple) { apple = document.createElement('link'); apple.rel = 'apple-touch-icon'; document.head.appendChild(apple); }
    apple.href = icon;
    try { localStorage.setItem('ff_touch_icon', icon); } catch { /* storage blocked */ }
  }

  /**
   * Swap the tab icon. Safari keeps the icon it first loaded and ignores an `href`
   * change on the existing <link>, so the old icon links are removed and fresh ones
   * inserted (with an explicit type, which Safari also wants). `shortcut icon` covers
   * older Safari versions.
   */
  private setFavicon(href: string): void {
    // Remember it for the next visit. On brand hosts index.html links the API's favicon redirect
    // instead, which Safari already gets at load; this remembered icon covers other hosts
    // (localhost). localStorage is per origin, so per brand subdomain.
    try { localStorage.setItem('ff_favicon', href); } catch { /* private mode / storage blocked */ }
    const current = document.querySelector('link[rel="icon"]') as HTMLLinkElement | null;
    if (current && current.href === new URL(href, document.baseURI).href) return; // already showing it
    document.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"]').forEach((l) => l.remove());
    const type = faviconType(href);
    for (const rel of ['icon', 'shortcut icon']) {
      const link = document.createElement('link');
      link.rel = rel;
      link.href = href;
      if (type) link.type = type;
      document.head.appendChild(link);
    }
  }

  /** Trigger the native install prompt. Resolves with the customer's choice. */
  async install(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
    if (!this.deferred) return 'unavailable';
    await this.deferred.prompt();
    const choice = await this.deferred.userChoice;
    this.deferred = null;
    this._canInstall.next(false);
    return choice?.outcome ?? 'dismissed';
  }
}

/** MIME type from the icon URL's extension (query/hash ignored); '' when unknown. */
function faviconType(href: string): string {
  const ext = (href.split(/[?#]/)[0].split('.').pop() || '').toLowerCase();
  return ({ png: 'image/png', ico: 'image/x-icon', svg: 'image/svg+xml', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif', webp: 'image/webp' } as Record<string, string>)[ext] || '';
}
